/* eslint-disable react-hooks/exhaustive-deps */

import { FolderOutlineOpenIcon } from '@tableau/icons/FolderOutlineOpenIcon';
import { SentinelPermissionsRequiredId } from '@tableau/nested-project-explorer';
import { TreeInstance, TreeItem, TreeWidget } from '@tableau/widgets';
import React, {forwardRef, RefObject, useMemo} from 'react';
import { classes } from 'typestyle';

import { Location } from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { Redux } from '~src/settings/general-settings/utils';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { useEPT } from '~src/spacesPlatform/hooks/useEPT';
import { useWindowSize } from '~src/spacesPlatform/hooks/useWindowSize';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { getSiteName } from '~src/uploadContent/utils/ProjectExplorerEffects';

import { IDestinationProject } from '../ServerApi/types/IDestinationProject';
import { minWindowSizeToEnableProjectTree, projectTreeFeatureName, ProjectTreeService } from '../services/ProjectTreeService';
import { ProjectHooks } from '../services/Queries';
import { ProjectDefinition } from './contentDefinitions/ProjectDefinition';
import { ProjectTreeResizer } from './ProjectTreeResizer';
import { projectTreeNodeStyle, projectTreeNodeTextContainerStyle, projectTreeNodeTextStyle, projectTreeResizableFocusStyle, projectTreeResizableHoverStyle, projectTreeResizableStyle, projectTreeStyle } from './styles/ProjectTreeStyles';

export type ProjectTreeItem = TreeItem & { projectName: string, luid: string };

const getContent = (projectName: string) => (
    <div className={projectTreeNodeStyle} title={projectName}>
        <FolderOutlineOpenIcon size={18} />
        <div className={projectTreeNodeTextContainerStyle}>
            <span className={projectTreeNodeTextStyle}>
                {projectName}
            </span>
        </div>
    </div>
);

function convertProjectsToProjectTreeItems(projects: IDestinationProject[]) {
    const projectTreeItems: ProjectTreeItem[] | null = (projects && projects.length > 0) ? [] : null;
    const projectById: Record<string, IDestinationProject> = {};
    let addPermissonsRequiredProjectItem: boolean = false;

    projects && projects.forEach(project => {
        projectById[project.id] = project;
    });

    projects && projects.forEach(p => {
        let parentValue = p.parentProjectId;
        if (p.parentProjectId && !projectById[parentValue]) {
            addPermissonsRequiredProjectItem = true;
            parentValue = SentinelPermissionsRequiredId;
        }
        const item: ProjectTreeItem = {
            content: getContent(p.name),
            projectName: p.name,
            value: p.id.toString(),
            luid: p.luid,
            parentValue: parentValue?.toString()
        };
        projectTreeItems.push(item);
    });
    if (projectTreeItems && addPermissonsRequiredProjectItem) {
        projectTreeItems.push({
            content: getContent(Messages.permissionsRequiredProjectName()),
            projectName: Messages.permissionsRequiredProjectName(),
            value: SentinelPermissionsRequiredId,
            luid: '',
            disabled: true,
        });
    }
    return projectTreeItems;
}

const itemFilterFn = (item: ProjectTreeItem, searchText: string) => {
    return item.projectName.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
};

export function refContainsFocus(ref: RefObject<any>) {
  return ref.current?.contains(document.activeElement);
}

export function ProjectTree(): JSX.Element {
  const [projectTreeToggleState, setProjectTreeToggleState] = React.useState('off');

  const spaceInfo = Redux.useSelector(selectSpaceInfo);
  const enabled = ProjectTreeService.instance.isProjectTreeEnabled(spaceInfo);

  const treeRef = React.useRef<HTMLDivElement | null>(null);
  const searchBarRef = ProjectTreeService.instance.useSearchBarRef();

  const [windowSize] = useWindowSize();

  const shouldRender = enabled && projectTreeToggleState === 'on' && windowSize.width >= minWindowSizeToEnableProjectTree;
  const [prevShouldRender, setPrevShouldRender] = React.useState(shouldRender);
  const [treeIsFocused, setTreeIsFocused] = React.useState(null);

  React.useEffect(() => {
    // manually track the focused element since useEffect will run after render
    // and document.activeElement will already be lost when the tree collapses
    const handleFocusChange = (e: FocusEvent | null) => {
      if (refContainsFocus(treeRef)) {
        setTreeIsFocused(true);
      }
      else {
        setTreeIsFocused(false);
      }
    };

    document.addEventListener('focusin', handleFocusChange);

    if (treeRef.current?.contains(document.activeElement)) {
      setTreeIsFocused(true);
    }
    else {
      setTreeIsFocused(false);
    }

    // clean up event handler on unmount
    return () => {
      document.removeEventListener('focusin', handleFocusChange);
    };
  }, []);

  React.useEffect(() => {
    if (!shouldRender && prevShouldRender) {
      // focus was in the tree and lost after the tree collapsed
      if (treeIsFocused) {
        searchBarRef.current.querySelector('input').focus();
      }
    }

    setPrevShouldRender(shouldRender);
  }, [shouldRender]);

  React.useEffect(() => {
        let isMounted = true;

        if (enabled) {
            ProjectTreeService.instance.onProjectTreeToggleStateChanged.push(toggleState => {
                if (isMounted) {
                    setProjectTreeToggleState(toggleState);
                    if (toggleState === 'on') {
                        ProjectTreeService.instance.sendProjectTreeUsageTelemetryEvent('impression');
                    }
                }
            });
            ProjectTreeService.instance.initialize();
        }

        return () => {
            isMounted = false;
        };
    }, [enabled]);

  return shouldRender && <ProjectTreeContent ref={treeRef} />;
}

const ProjectTreeContent = forwardRef<HTMLDivElement, {}>((props, ref) => {
    const siteName = getSiteName();
    const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
    const spaceInfo = Redux.useSelector(selectSpaceInfo);
    const selectedProjectIdFromURL = (spaceInfo?.spaceType === 'project' && spaceInfo.item?.id) ? spaceInfo.item.id : null;
    const urls = new SpacesSiteUrls({ siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName() });
    const { isLoading, destinationProjects } = ProjectHooks.useDestinationProjects(siteName);
    const projectTreeItems = useMemo(() => convertProjectsToProjectTreeItems(destinationProjects), [isLoading, destinationProjects]);
    const projectTreeRef = React.useRef<TreeInstance>(null);

    // Resizable Project Tree ================================
    const [treeWidth, setTreeWidth] = React.useState<number>(() => ProjectTreeService.instance.loadStoredTreeWidth());
    const isResizableEnabled = ProjectTreeService.instance.isResizableProjectTreeEnabled();
    const [isDividerHoverActive, setIsDividerHoverActive] = React.useState(false);
    const [isDividerFocusActive, setIsDividerFocusActive] = React.useState(false);

    const handleWidthChange = React.useCallback((newWidth: number) => {
        setTreeWidth(newWidth);
    }, []);
    // =======================================================

    /**
     * Inline anonymous function is not used to prevent unnecessary re-renders in TreeWidget filtering
     * TreeWidget has a filter hook that updates the expansion state, which depends on itemFilterFn.
     * When a project is selected, it causes a re-render, and itemFilterFn is generated as a new function on every render.
     * This leads to the filter hook being triggered unnecessarily.
     */

    const handleSelectionChange = (selectedId: string | null) => {
        if (selectedId) {
            ProjectTreeService.instance.sendProjectTreeUsageTelemetryEvent('click_through');
            const selectedProjectTreeItem: ProjectTreeItem = projectTreeItems?.find(item => item.value === selectedId);
            if (selectedProjectTreeItem) {
                setSelectedProjectId(selectedId);
                Location.instance.setUrl(ProjectDefinition.contentSpaceUrl(urls, selectedProjectTreeItem.value, selectedProjectTreeItem.luid));
            }
        }
    };

    React.useEffect(() => {
        const treeNodeExpandedValues = ProjectTreeService.instance.getTreeNodeExpandedValues();
        if (treeNodeExpandedValues && projectTreeRef.current) {
            projectTreeRef.current.expandedValues = treeNodeExpandedValues;
        }

        return () => {
            if (projectTreeRef.current) {
                ProjectTreeService.instance.saveTreeNodeExpandedValues(projectTreeRef.current.expandedValues);
            }
        };
    }, []);

    React.useEffect(() => {
        if (selectedProjectId && projectTreeRef.current && isLoading === false) {
            projectTreeRef.current.scrollIntoView(selectedProjectId, 'center');
        }
    }, [selectedProjectId, isLoading]);

    React.useEffect(() => {
        setSelectedProjectId(selectedProjectIdFromURL);
    }, [selectedProjectIdFromURL]);

    useEPT(projectTreeFeatureName, destinationProjects && destinationProjects.length > 0, destinationProjects && destinationProjects.length);

    if (isResizableEnabled) {
        const containerStyles = classes(
            projectTreeResizableStyle,
            isDividerHoverActive && projectTreeResizableHoverStyle,
            isDividerFocusActive && projectTreeResizableFocusStyle
        );

        const widthOverrideStyle = {
            width: treeWidth
        };

        return <div ref={ref} className={containerStyles} style={widthOverrideStyle} data-tb-test-id='project-tree-container'>
            <TreeWidget<ProjectTreeItem>
                itemFilterFn={itemFilterFn}
                items={projectTreeItems}
                onSelectionChange={(selectedProjectId: string | null) => handleSelectionChange(selectedProjectId)}
                onSearchTextChange={() => projectTreeRef.current.scrollIntoView(selectedProjectId, 'center')}
                selectedValue={selectedProjectId}
                style={{ height: '100%' }}
                treeRef={projectTreeRef}
                testId='ProjectTree'
                label={Messages.searchProjects()}
            />
            <ProjectTreeResizer
                containerRef={ref as React.RefObject<HTMLDivElement | null>}
                onWidthChange={handleWidthChange}
                onHoverChange={setIsDividerHoverActive}
                onFocusChange={setIsDividerFocusActive}
            />
        </div>;
    }
    else {
        return <div ref={ref} className={projectTreeStyle} data-tb-test-id='project-tree-container'>
            <TreeWidget<ProjectTreeItem>
                itemFilterFn={itemFilterFn}
                items={projectTreeItems}
                onSelectionChange={(selectedProjectId: string | null) => handleSelectionChange(selectedProjectId)}
                onSearchTextChange={() => projectTreeRef.current.scrollIntoView(selectedProjectId, 'center')}
                selectedValue={selectedProjectId}
                style={{ height: '100%' }}
                treeRef={projectTreeRef}
                testId='ProjectTree'
                label={Messages.searchProjects()}
            />
        </div>;
    }
});